// Owner: shell-nav (plan §9.2). The authenticated app frame: the rail (collapsible, peeking), the
// connectivity banner above the content, the mobile tab bar, the shell's keyboard scope (⌘\, pin,
// copy link), recent-item tracking and document titles. `_app.tsx` renders it around its routes, and
// the root's 404 renders it around the not-found page for signed-in members.
import { useRouterState } from '@tanstack/react-router';
import { type ReactNode, Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useCommandScope } from '@dataroom/interaction';
import { copyWithToast, getLayerStack } from '@dataroom/ui';
import { type ShellCommandContext, shellScope } from '../features/search/commands';
import { t } from '../lib/i18n';
import { preloadCommandPalette } from '../overlays/command-palette';
import { useRealtimeChannel } from '../lib/realtime-channel';
import { AppSidebar } from './AppSidebar';
import { ConnectivityBanner } from './connectivity';
import { MobileTabBar } from './MobileTabBar';
import { PathnameProvider } from './pathname';
import { useCurrentPin } from './pins';
import { useEditorBottomBarOwned, useRailEditorFocused, useRailState } from './rail-state';
import { useTrackRecent } from './recent';
import { useAppBrand } from '../features/branding/app-brand';
import { usePreviewState } from '../features/copilot/preview-store';

/** The bar of preview mode (a change the Copilot waits to apply, shown in the app); loaded when one is shown. */
const PreviewBar = lazy(() => import('../features/copilot/PreviewBar'));

const PEEK_DELAY_MS = 140;

/** The rail peeks open while the pointer or keyboard focus is in it (only when collapsed). */
function usePeek(enabled: boolean) {
  const [peek, setPeek] = useState(false);
  const hover = useRef(false);
  const focus = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // A navigation from the peeking rail closes it.
  useEffect(() => setPeek(false), [pathname]);
  useEffect(() => {
    if (!enabled) setPeek(false);
  }, [enabled]);
  // A menu opened from the peeking rail keeps it open until the menu closes.
  useEffect(() => {
    if (!peek) return;
    const stack = getLayerStack();
    return stack.subscribe(() => {
      if (stack.list().length === 0 && !hover.current && !focus.current) setPeek(false);
    });
  }, [peek]);
  const settle = () => {
    window.clearTimeout(timer.current);
    if (hover.current || focus.current) {
      timer.current = window.setTimeout(() => setPeek(true), hover.current ? PEEK_DELAY_MS : 0);
    } else if (getLayerStack().list().length === 0) {
      setPeek(false);
    }
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const handlers = enabled
    ? {
        onPointerEnter: (e: React.PointerEvent) => {
          if (e.pointerType === 'touch') return;
          hover.current = true;
          settle();
        },
        onPointerLeave: () => {
          hover.current = false;
          settle();
        },
        onFocus: () => {
          focus.current = true;
          settle();
        },
        onBlur: (e: React.FocusEvent) => {
          if (e.currentTarget.contains(e.relatedTarget)) return;
          focus.current = false;
          settle();
        },
      }
    : {};
  return { peek: enabled && peek, handlers };
}

/** Keeps the `shell` keyboard scope active with the rail and the open page as context. */
function useShellCommands(rail: ReturnType<typeof useRailState>) {
  const pin = useCurrentPin();
  const latest = useRef({ rail, pin });
  latest.current = { rail, pin };
  const ctx = useMemo<ShellCommandContext>(
    () => ({
      railCollapsed: () => latest.current.rail.collapsed,
      toggleRail: () => latest.current.rail.toggle(),
      pinnable: () => latest.current.pin.ref !== null,
      pinned: () => latest.current.pin.pinned,
      togglePin: () => latest.current.pin.toggle(),
      copyLink: () => {
        void copyWithToast(window.location.href, { title: t('shell.link.copied') });
      },
    }),
    [],
  );
  useCommandScope(shellScope, { ctx });
}

export function AppShell({ children }: { children: ReactNode }) {
  useRealtimeChannel();
  useTrackRecent();
  // The workspace brand kit's accent and mark (Settings › Branding, owner feedback F3).
  useAppBrand();
  const ownRail = useRailState();
  // An open editor folds the rail to its icons (the member's own choice is kept).
  const editorFocused = useRailEditorFocused();
  const bottomBarOwned = useEditorBottomBarOwned();
  const rail = useMemo(
    () => (editorFocused && !ownRail.collapsed ? { ...ownRail, collapsed: true } : ownRail),
    [editorFocused, ownRail],
  );
  useShellCommands(rail);
  useEffect(() => preloadCommandPalette(), []);
  const { peek, handlers } = usePeek(rail.collapsed);
  const state = rail.collapsed ? (peek ? 'peek' : 'collapsed') : 'expanded';
  const preview = usePreviewState().session;
  return (
    <PathnameProvider>
      <div className="app" data-rail={state} data-editor-bar={bottomBarOwned ? '' : undefined}>
        <div className="rail-slot" {...handlers}>
          <AppSidebar rail={rail} peek={peek} />
        </div>
        <div className="app-main">
          <ConnectivityBanner />
          {preview ? (
            <Suspense fallback={null}>
              <PreviewBar />
            </Suspense>
          ) : null}
          {children}
        </div>
        <MobileTabBar />
      </div>
    </PathnameProvider>
  );
}
